/**
 * Widget registry — template-id → component map used by the slot
 * renderer. Decoupled from any specific widget so the catalogue lives
 * in the same files where the widgets are defined (P5/P6). This module
 * only ships the mechanism.
 *
 * A registration carries:
 *   - `id`        — the component-flag id used by `<ComponentGate>`. Often
 *                   matches the template id, but we keep them separate so
 *                   one component can serve multiple templates (e.g.
 *                   `skytteliga` + `standalone-skytteliga`).
 *   - `component` — the React component, typed `({data}) => ReactElement`.
 *
 * `buildWidgetRegistry` throws on duplicate template keys so a P5/P6
 * registration mistake fails loudly at module load instead of producing
 * a silently-overwritten registry at runtime.
 */

import type { ComponentType } from 'react';
import { reportEvent } from '../../observability.js';

export interface RegisteredWidget<TData = unknown> {
  /** Component flag id used by ComponentGate. */
  id: string;
  /** React component. Receives `{data: TData}`. */
  component: ComponentType<{ data: TData }>;
}

export type WidgetRegistry = ReadonlyMap<string, RegisteredWidget>;

/**
 * Build a registry from a list of registrations. Throws on duplicate
 * template keys — silent overwrites would hide bugs.
 */
export function buildWidgetRegistry(
  entries: ReadonlyArray<readonly [template: string, widget: RegisteredWidget]>,
): WidgetRegistry {
  const map = new Map<string, RegisteredWidget>();
  for (const [template, widget] of entries) {
    if (map.has(template)) {
      throw new Error(
        `Duplicate widget registration for template "${template}"`,
      );
    }
    map.set(template, widget);
  }
  return map;
}

/**
 * Process-lifetime set of templates we have already warned about so a
 * single typo in a feed doesn't flood the console on every poll.
 */
const warnedUnknownTemplates = new Set<string>();

/** Warn once per unknown template id during development. */
export function warnUnknownTemplate(template: string): void {
  if (warnedUnknownTemplates.has(template)) {
    return;
  }
  warnedUnknownTemplates.add(template);
  reportEvent('widget-registry:unknown-template', { template });
}
